import type { DeepReadonly } from "@itandi/itandi-bb-ui";
import { RiDeleteBinLine } from "@itandi/itandi-bb-ui";
import { InputCheckBox } from "@itandi/itandi-bb-ui/InputCheckBox";
import { Label } from "@itandi/itandi-bb-ui/Label";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import { Td } from "@itandi/itandi-bb-ui/Td";
import { Tr } from "@itandi/itandi-bb-ui/Tr";
import React from "react";
import { DealClientTodoModal } from "~/components/common/deals/show/main/todos/DealClientTodoModal";
import { Dropdown } from "~/components/common/parts/dropdown/Dropdown";
import { DropdownList } from "~/components/common/parts/dropdown/DropdownList";
import { DropdownMenu } from "~/components/common/parts/dropdown/DropdownMenu";
import { ConfirmModal } from "~/components/common/parts/modal/ConfirmModal";
import { StatusIcon } from "~/components/deprecated/StatusIcon";
import { useDealContext } from "~/context/DealContext";
import { useDealTodos } from "~/hooks/deals/useDealTodos";
import { useOpenHandler } from "~/hooks/useOpenHandler";
import { useEnabledStaffs } from "~/hooks/useStaffs";
import type { DealTodo } from "~/models/DealTodo";
import { isSuccess } from "~/utils/api/api_handler";
import { destroy } from "~/utils/api/deals/DealTodo";
import { formatDate, getTodayISO8601 } from "~/utils/DateUtil";
import Styles from "./DealClientTodoTable.module.scss";

type Props = DeepReadonly<{
	handleCheck: (newTodo: DealTodo) => void;
	todo: DealTodo;
}>;

export const DealClientTodoTableRow: React.FC<Props> = ({
	todo,
	handleCheck
}: Props) => {
	const { notify } = useNotification();
	const deal = useDealContext();
	const { mutate: mutateTodos } = useDealTodos(deal.id);
	const staffs = useEnabledStaffs().data;
	const {
		isOpen: openDeleteConfirm,
		handleOpen: handleOpenDeleteConfirm,
		handleClose: handleCloseDeleteConfirm
	} = useOpenHandler();
	const {
		isOpen: openEditModal,
		handleOpen: handleOpenEditModal,
		handleClose: handleCloseEditModal
	} = useOpenHandler();
	const handleAcceptDelete = React.useCallback(async () => {
		const response = await destroy(deal.id, todo.id);
		if (isSuccess(response)) {
			await mutateTodos();
			handleCloseDeleteConfirm();
			notify({ content: "TODOを削除しました。", variant: "success" });
			return;
		}
		notify({ content: "エラーが発生しました。", variant: "error" });
	}, [deal.id, todo.id, notify, mutateTodos, handleCloseDeleteConfirm]);

	const onCheck = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>): void => {
			if (e.target.checked && todo.completedAt === null) {
				handleCheck({
					...todo,
					completed: true,
					completedAt: getTodayISO8601()
				});
				return;
			}
			handleCheck({ ...todo, completed: e.target.checked });
		},
		[handleCheck, todo]
	);
	if (staffs === null) {
		return null;
	}
	return (
		<Tr>
			<Td>
				<InputCheckBox checked={todo.completed} onChange={onCheck} />
			</Td>
			<Td>
				<button
					className={Styles.TodoLink}
					onClick={handleOpenEditModal}
					type="button"
				>
					{todo.title}
				</button>
				{openEditModal ? (
					<DealClientTodoModal
						deal={deal}
						handleClose={handleCloseEditModal}
						staffs={staffs}
						todo={todo}
					/>
				) : null}
			</Td>
			<Td>
				<Label color={todo.staff === null ? "warning" : undefined} size="xs">
					{todo.staff?.name ?? "設定なし"}
				</Label>
				{todo.staff === null && <StatusIcon variant="Alert" />}
			</Td>
			<Td>
				<Label color={todo.dueDate === null ? "subtle" : undefined} size="xs">
					{formatDate(todo.dueDate, true) ?? "未登録"}
				</Label>
			</Td>
			<Td>
				<Label
					color={todo.completedAt === null ? "subtle" : undefined}
					size="xs"
				>
					{formatDate(todo.completedAt) ?? "未完了"}
				</Label>
			</Td>
			<Td>
				<Dropdown title="操作" variant="normal">
					<DropdownList>
						<DropdownMenu onClick={handleOpenDeleteConfirm}>
							<span className={Styles.DeleteLink}>
								<RiDeleteBinLine className={Styles.DropdownMenuIcon} />
								TODOを削除
							</span>
						</DropdownMenu>
					</DropdownList>
				</Dropdown>
				<ConfirmModal
					confirmType="delete"
					handleAccept={handleAcceptDelete}
					handleCancel={handleCloseDeleteConfirm}
					isOpen={openDeleteConfirm}
				>
					削除してよろしいですか？
				</ConfirmModal>
			</Td>
		</Tr>
	);
};
